import { PERMISSIONS, RATE_PLAN_TYPE } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreateRatePlan, useUpdateRatePlan } from '../api/mutations.js';
import { useRatePlanDetail } from '../api/queries.js';
import { type RatePlanDetail, type RatePlanFormInput, ratePlanFormInput } from '../api/schemas.js';
import { RatePlanFields } from '../forms/rate-plan-fields.js';

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

const CREATE_DEFAULTS: RatePlanFormInput = {
  propertyId: '',
  code: '',
  name: '',
  type: RATE_PLAN_TYPE.STANDARD,
  description: '',
  currency: '',
  mealPlan: 'NONE',
  refundable: true,
  status: 'ACTIVE',
  roomTypeIds: [],
  minLengthOfStay: '',
  maxLengthOfStay: '',
  advanceBookingDays: '',
  activeFrom: '',
  activeTo: '',
  cancellationPolicyId: '',
  paymentPolicyId: '',
  pmsRateId: '',
};

function defaultsFrom(ratePlan: RatePlanDetail): RatePlanFormInput {
  return {
    propertyId: ratePlan.propertyId,
    code: ratePlan.code,
    name: ratePlan.name,
    type: ratePlan.type,
    description: ratePlan.description ?? '',
    currency: ratePlan.currency,
    mealPlan: ratePlan.mealPlan,
    refundable: ratePlan.refundable,
    status: ratePlan.status,
    roomTypeIds: [...ratePlan.roomTypeIds],
    minLengthOfStay: ratePlan.minLengthOfStay === null ? '' : String(ratePlan.minLengthOfStay),
    maxLengthOfStay: ratePlan.maxLengthOfStay === null ? '' : String(ratePlan.maxLengthOfStay),
    advanceBookingDays:
      ratePlan.advanceBookingDays === null ? '' : String(ratePlan.advanceBookingDays),
    activeFrom: ratePlan.activeFrom ?? '',
    activeTo: ratePlan.activeTo ?? '',
    cancellationPolicyId: ratePlan.cancellationPolicyId ?? '',
    paymentPolicyId: ratePlan.paymentPolicyId ?? '',
    pmsRateId: ratePlan.pmsRateId ?? '',
  };
}

export function RatePlanFormPage(): React.JSX.Element {
  const ratePlanId = useParams().ratePlanId;
  const isEdit = ratePlanId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.PRICING_UPDATE : PERMISSIONS.PRICING_UPDATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditRatePlan ratePlanId={ratePlanId} /> : <CreateRatePlan />}
    </PermissionGuard>
  );
}

function CreateRatePlan(): React.JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const create = useCreateRatePlan();

  const form = useAppForm<RatePlanFormInput>({
    schema: ratePlanFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Rate plan created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate({ pathname: `/rate-plans/${created.id}`, search });
    },
  });

  return (
    <FormPage
      title="New rate plan"
      description="A rate plan groups nightly rates by currency, room types and cancellation rules."
      backTo="/rate-plans"
      backLabel="Rate plans"
      submitLabel="Create rate plan"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RatePlanFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="create"
      />
    </FormPage>
  );
}

function EditRatePlan({ ratePlanId }: { readonly ratePlanId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const update = useUpdateRatePlan();
  const query = useRatePlanDetail(ratePlanId);

  if (query.isPending) return <LoadingState label="Loading rate plan" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditRatePlanForm
      ratePlan={query.data}
      onSaved={() => {
        void navigate({ pathname: `/rate-plans/${ratePlanId}`, search });
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ ratePlanId, version: query.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditRatePlanForm({
  ratePlan,
  save,
  onSaved,
}: {
  readonly ratePlan: RatePlanDetail;
  readonly save: (values: RatePlanFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<RatePlanFormInput>({
    schema: ratePlanFormInput,
    defaultValues: defaultsFrom(ratePlan),
    successMessage: 'Rate plan updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${ratePlan.name}`}
      description="Changes take effect immediately for every rate loaded under this plan."
      backTo="/rate-plans"
      backLabel="Rate plans"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RatePlanFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="edit"
      />
    </FormPage>
  );
}
